import classnames from 'classnames';
import React, { forwardRef, Ref, useId } from 'react';

import '@eika/radio-button-style/src/ButtonToggles.scss';

import ErrorMessageLabel from './ErrorMessageLabel';
import { RadioButtonLabel } from './RadioButtonLabel';
import { ButtonToggleProps } from './Types';
import { toDataTrackIdFormat } from './utils/toDataTrackIdFormat';

const ButtonToggles = forwardRef(
    (
        {
            className,
            selectedOption,
            options,
            onChange,
            label,
            sublabel,
            infoButton,
            externalLinkLabel,
            errorMessage,
            required = false,
            small = false,
            dataTrackId,
            ...restTop
        }: ButtonToggleProps,
        ref: Ref<HTMLInputElement>,
    ): JSX.Element => {
        const errorId = useId();
        const targetDataTrackId = dataTrackId ?? (typeof label === 'string' ? label : undefined);

        // Need to wrap component in a div so that it works properly when there is a flex/grid container around it on safari :/
        return (
            <div>
                <fieldset
                    className={classnames('sgw-button-toggles', className)}
                    aria-describedby={errorId}
                    aria-required={required}
                >
                    <RadioButtonLabel
                        label={label}
                        sublabel={sublabel}
                        infoButton={infoButton}
                        externalLinkLabel={externalLinkLabel}
                    />
                    <div
                        className={classnames('sgw-button-toggle-options', {
                            'sgw-button-toggle-options--error': !!errorMessage,
                        })}
                        role="group"
                        {...restTop}
                    >
                        {options.map(({ value, label, id, ref, ariaLabel, dataTrackLinkname, ...rest }) => {
                            const targetDataTrackLinkname =
                                dataTrackLinkname || (typeof label === 'string' ? label : undefined) || value;
                            return (
                                <React.Fragment key={value}>
                                    {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
                                    <button
                                        className={classnames('sgw-button-toggle-option', {
                                            'sgw-button-toggle-option__selected': value === selectedOption,
                                            'sgw-button-toggle-option__small': small,
                                            'sgw-button-toggle-option__error': !!errorMessage,
                                            disabled: rest.disabled,
                                        })}
                                        value={value}
                                        onClick={() => onChange(value)}
                                        id={id}
                                        ref={ref}
                                        type="button"
                                        aria-pressed={value === selectedOption}
                                        aria-invalid={!!errorMessage}
                                        aria-label={ariaLabel}
                                        data-track-id={toDataTrackIdFormat(targetDataTrackId)}
                                        data-track-linkname={toDataTrackIdFormat(targetDataTrackLinkname)}
                                        {...rest}
                                    >
                                        {label || value}
                                    </button>
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <input type="hidden" value={selectedOption ?? ''} ref={ref} />
                    <ErrorMessageLabel errorMessage={errorMessage} errorId={errorId} />
                </fieldset>
            </div>
        );
    },
);

export default ButtonToggles;
